import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Trash2, RotateCcw, ShieldAlert, RefreshCw, AlertTriangle, Search } from 'lucide-react';

// ★ 2026-08-11 (Fase 37) — CESTINO LEAD
// Soft-delete: i lead cancellati restano recuperabili per 30 giorni.
// Solo admin/amministrativo possono accedere.

function daysColor(daysLeft) {
  if (daysLeft <= 3) return 'text-red-600 dark:text-red-400';
  if (daysLeft <= 7) return 'text-amber-600 dark:text-amber-400';
  return 'text-muted-foreground';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function TrashPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [search, setSearch] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [purging, setPurging] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null); // lead in attesa di conferma purge
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  const isAdmin = ['admin', 'amministrativo'].includes(user?.role);

  const fetchTrash = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const { data } = await api.get('/leads/trash?limit=500');
      setItems(data?.leads || []);
      setRetentionDays(data?.retention_days || 30);
    } catch (e) {
      toast.error("Impossibile caricare il cestino");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchTrash();
  }, [fetchTrash]);

  const handleRestore = async (lead) => {
    try {
      setRestoring(lead.id);
      await api.post(`/leads/${lead.id}/restore`);
      toast.success(`Lead "${lead.name || lead.email}" ripristinato`);
      await fetchTrash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ripristino fallito');
    } finally {
      setRestoring(null);
    }
  };

  const handlePurge = async (lead) => {
    try {
      setPurging(lead.id);
      const { data } = await api.delete(`/leads/${lead.id}/purge`);
      const cleaned = Object.entries(data?.deleted || {})
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}:${v}`)
        .join(' · ');
      toast.success(`Cancellato definitivamente. ${cleaned || 'Nessun dato figlio.'}`);
      await fetchTrash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancellazione fallita');
    } finally {
      setPurging(null);
    }
  };

  const handleEmptyTrash = async () => {
    try {
      setEmptyingTrash(true);
      const { data } = await api.post('/leads/trash/empty?force=true');
      toast.success(`Cestino svuotato: ${data?.purged || 0} lead cancellati`);
      setConfirmEmpty(false);
      await fetchTrash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Svuotamento fallito');
    } finally {
      setEmptyingTrash(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((l) =>
      (l.name || '').toLowerCase().includes(s) ||
      (l.email || '').toLowerCase().includes(s) ||
      (l.phone || '').toLowerCase().includes(s),
    );
  }, [items, search]);

  const expiringSoon = useMemo(
    () => items.filter((l) => (l.trash_days_left ?? 30) <= 3).length,
    [items],
  );

  if (!isAdmin) {
    return (
      <div className="p-6" data-testid="trash-forbidden">
        <Alert variant="destructive">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>Accesso negato</AlertTitle>
          <AlertDescription>Solo utenti admin possono vedere il cestino.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="trash-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-[hsl(var(--primary))]" />
            <h1 className="font-serif text-2xl">Cestino Lead</h1>
            <Badge variant="secondary" data-testid="trash-count">
              {items.length} {items.length === 1 ? 'lead' : 'lead'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            I lead cancellati restano qui per <b>{retentionDays} giorni</b>: puoi ripristinarli in un click.
            Dopo questo periodo vengono cancellati definitivamente insieme a incassi, pagamenti,
            ordini e tutti i dati collegati.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTrash}
            disabled={loading}
            data-testid="trash-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          {items.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmEmpty(true)}
              data-testid="trash-empty-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Svuota cestino
            </Button>
          )}
        </div>
      </div>

      {/* Alert lead in scadenza */}
      {expiringSoon > 0 && (
        <Alert variant="destructive" data-testid="trash-expiring-alert">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>{expiringSoon} lead in scadenza (≤ 3 giorni)</AlertTitle>
          <AlertDescription>
            Se non li ripristini in tempo, verranno cancellati definitivamente insieme
            a tutti i dati collegati.
          </AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, email, telefono…"
          className="pl-9"
          data-testid="trash-search-input"
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={`sk-${i}`} className="h-28 w-full" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && items.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Trash2 className="w-10 h-10 mx-auto text-muted-foreground opacity-40 mb-3" />
            <p className="text-sm text-muted-foreground">
              Il cestino è vuoto. I lead cancellati appariranno qui e resteranno recuperabili per {retentionDays} giorni.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Search returned nothing */}
      {!loading && items.length > 0 && filtered.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nessun lead trovato con &ldquo;{search}&rdquo;.
          </CardContent>
        </Card>
      )}

      {/* Lista lead nel cestino */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((lead) => {
            const daysLeft = lead.trash_days_left ?? 30;
            return (
              <Card
                key={lead.id}
                className="relative overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`trash-item-${lead.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Nome + email */}
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0">
                      <div className="font-semibold truncate" data-testid={`trash-name-${lead.id}`}>
                        {lead.name || <span className="text-muted-foreground italic">Senza nome</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {lead.email || '—'} {lead.phone ? `· ${lead.phone}` : ''}
                      </div>
                    </div>
                    <Badge
                      variant={daysLeft <= 3 ? 'destructive' : daysLeft <= 7 ? 'default' : 'secondary'}
                      className="shrink-0"
                      data-testid={`trash-days-${lead.id}`}
                    >
                      {daysLeft === 0 ? 'Scade oggi' : `${daysLeft} gg`}
                    </Badge>
                  </div>

                  {/* Metadata */}
                  <div className="text-[11px] text-muted-foreground space-y-0.5">
                    <div>
                      <span className="opacity-70">Stage al momento della cancellazione: </span>
                      <span className="font-medium">{lead.stage || '—'}</span>
                    </div>
                    <div>
                      <span className="opacity-70">Cancellato il: </span>
                      <span>{formatDate(lead.deleted_at)}</span>
                    </div>
                    {lead.deleted_by_name && (
                      <div>
                        <span className="opacity-70">Da: </span>
                        <span>{lead.deleted_by_name}</span>
                      </div>
                    )}
                    <div className={daysColor(daysLeft)}>
                      Scade il: {formatDate(lead.trash_expires_at)}
                    </div>
                  </div>

                  {/* Azioni */}
                  <div className="flex items-center gap-2 pt-2 border-t border-border">
                    <Button
                      size="sm"
                      variant="default"
                      className="flex-1"
                      onClick={() => handleRestore(lead)}
                      disabled={restoring === lead.id || purging === lead.id}
                      data-testid={`trash-restore-${lead.id}`}
                    >
                      <RotateCcw className={`w-3.5 h-3.5 mr-1.5 ${restoring === lead.id ? 'animate-spin' : ''}`} />
                      Ripristina
                    </Button>
                    <AlertDialog>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPurgeTarget(lead)}
                        disabled={restoring === lead.id || purging === lead.id}
                        data-testid={`trash-purge-${lead.id}`}
                      >
                        <Trash2 className={`w-3.5 h-3.5 mr-1.5 ${purging === lead.id ? 'animate-pulse' : ''}`} />
                        Elimina definitivamente
                      </Button>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirm Svuota cestino */}
      <AlertDialog open={confirmEmpty} onOpenChange={setConfirmEmpty}>
        <AlertDialogContent data-testid="trash-empty-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Svuotare completamente il cestino?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno cancellati definitivamente <b>{items.length}</b> lead e tutti i loro
              dati collegati (incassi, pagamenti, ordini, attività, WhatsApp, email).
              <br />
              <span className="text-red-600 font-medium mt-2 block">
                Questa operazione è irreversibile.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={emptyingTrash}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEmptyTrash}
              disabled={emptyingTrash}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="trash-empty-confirm-btn"
            >
              {emptyingTrash ? 'Svuotamento…' : 'Sì, svuota tutto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm purge singolo lead */}
      <AlertDialog open={!!purgeTarget} onOpenChange={(open) => !open && setPurgeTarget(null)}>
        <AlertDialogContent data-testid="trash-purge-single-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancellare definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Il lead <b>{purgeTarget?.name || purgeTarget?.email}</b> e tutti i suoi dati
              (incassi, pagamenti, ordini proposte, attività, WhatsApp, email) verranno
              eliminati per sempre. <b>Non è possibile annullare</b>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!purging}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (purgeTarget) {
                  await handlePurge(purgeTarget);
                  setPurgeTarget(null);
                }
              }}
              disabled={!!purging}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="trash-purge-single-confirm-btn"
            >
              {purging ? 'Cancellazione…' : 'Sì, cancella per sempre'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
